import React from 'react';

/**
 * Live preview of the Proforma Invoice PDF the backend renders.
 * Mirrors `proforma_invoice.html` (Django template) so what the user sees here
 * matches the generated PDF. All CSS is scoped under `.pi-preview` so it cannot
 * leak into the surrounding Tailwind UI.
 */

const COMPANY = {
    name: 'RASHAIL TECH LABS PRIVATE LIMITED',
    addressLines: [
        'S1, Malwa Tower, Near ICICI Bank, Ashok Nagar,',
        'Indore, Madhya Pradesh - 452001',
    ],
    gstin: '23AAJCR6974Q1ZG',
    pan: 'AAJCR6974Q',
    logo: 'https://rashailagro.in/assets/icons/Rashail-agro-logo.png',
    whatsapp: '+91 83495 03619',
    email: 'rashailagro@gmail.com',
    website: 'www.rashailagro.in',
};

const MIN_ITEM_ROWS = 5;

const money = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '0.00';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (value) => {
    const date = value ? new Date(value) : new Date();
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const CSS = `
.pi-preview {
    font-family: 'Noto Sans', 'Noto Sans Devanagari', Arial, Helvetica, sans-serif;
    color: #333;
    font-size: 12px;
    line-height: 1.4;
    background: #fff;
    width: 100%;
    max-width: 750px;
    margin: 0 auto;
    padding: 20px;
}
.pi-preview * { margin: 0; padding: 0; box-sizing: border-box; }

.pi-preview .pi-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding-bottom: 8px;
    border-bottom: 3px solid #0d7a3f;
}
.pi-preview .pi-company { flex: 1; }
.pi-preview .pi-company h2 { font-size: 15px; font-weight: 700; margin-bottom: 4px; color: #222; }
.pi-preview .pi-company p { font-size: 10px; color: #555; margin: 1px 0; }
.pi-preview .pi-title { text-align: right; }
.pi-preview .pi-title h1 {
    font-size: 24px; font-weight: 900; font-style: italic;
    color: #0d7a3f; letter-spacing: 1px; white-space: nowrap;
}
.pi-preview .pi-green-bar { height: 4px; background-color: #0d7a3f; }

.pi-preview .pi-customer-section {
    display: flex; justify-content: space-between; gap: 16px;
    margin-top: 20px; padding-bottom: 10px;
}
.pi-preview .pi-customer { flex: 1; min-width: 0; }
.pi-preview .pi-customer h3 { font-size: 18px; font-weight: 700; margin-bottom: 5px; word-break: break-word; }
.pi-preview .pi-customer p { font-size: 11px; color: #555; margin: 2px 0; word-break: break-word; }
.pi-preview .pi-customer .pi-gst { font-weight: 700; margin-top: 5px; font-size: 11px; color: #333; }
.pi-preview .pi-meta { text-align: right; white-space: nowrap; }
.pi-preview .pi-meta .pi-meta-label {
    font-size: 11px; font-weight: 700; color: #555; text-transform: uppercase;
}
.pi-preview .pi-meta .pi-meta-value {
    font-size: 14px; font-weight: 700; color: #222; margin-bottom: 10px;
}

.pi-preview .pi-items { width: 100%; border-collapse: collapse; margin-top: 15px; }
.pi-preview .pi-items thead tr { background-color: #1a5632; color: #fff; }
.pi-preview .pi-items th {
    padding: 8px 6px; font-size: 10px; text-transform: uppercase;
    text-align: center; font-weight: 700;
}
.pi-preview .pi-items th:nth-child(2) { text-align: left; }
.pi-preview .pi-items td {
    padding: 8px 6px; font-size: 11px; text-align: center; border-bottom: 1px solid #e0e0e0;
}
.pi-preview .pi-items td:nth-child(2) { text-align: left; word-break: break-word; }
.pi-preview .pi-items tbody tr:nth-child(even) { background-color: #f9f9f9; }
.pi-preview .pi-items .pi-empty-row td { padding: 15px 6px; }
.pi-preview .pi-items .pi-totals-row td {
    font-weight: 700; border-top: 2px solid #1a5632; padding: 8px 6px; background: #fff;
}
.pi-preview .pi-nowrap { white-space: nowrap; }

.pi-preview .pi-summary { margin-top: 15px; }
.pi-preview .pi-summary table { width: 100%; border-collapse: collapse; }
.pi-preview .pi-summary-label {
    text-align: right; font-weight: 700; font-size: 11px; color: #333; padding: 4px 15px;
}
.pi-preview .pi-summary-value {
    text-align: right; font-size: 11px; padding: 4px 10px; min-width: 100px; white-space: nowrap;
}
.pi-preview .pi-grand-total {
    background-color: #f0f0f0; border-top: 2px solid #1a5632; border-bottom: 2px solid #1a5632;
}
.pi-preview .pi-grand-total .pi-summary-label,
.pi-preview .pi-grand-total .pi-summary-value { font-size: 14px; font-weight: 900; padding: 8px 15px; }

.pi-preview .pi-notice {
    margin-top: 20px; padding: 10px; background-color: #fff8e1;
    border: 1px solid #f9a825; border-radius: 4px;
    font-size: 11px; color: #555; text-align: center; font-style: italic;
}

.pi-preview .pi-footer {
    margin-top: 30px; display: flex; justify-content: space-between; align-items: flex-end;
    gap: 16px; border-top: 3px solid #0d7a3f; padding-top: 15px;
}
.pi-preview .pi-terms { flex: 1; }
.pi-preview .pi-terms h4 { font-size: 11px; font-weight: 700; margin-bottom: 5px; text-transform: uppercase; }
.pi-preview .pi-terms ul { padding-left: 15px; font-size: 9px; color: #555; }
.pi-preview .pi-terms ul li { margin: 2px 0; }
.pi-preview .pi-branding { text-align: right; }
.pi-preview .pi-branding img { height: 40px; margin-bottom: 3px; }
.pi-preview .pi-brand-tagline { font-size: 9px; color: #555; font-style: italic; }

.pi-preview .pi-contact {
    display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap;
    margin-top: 15px; padding-top: 8px; border-top: 1px solid #ccc; font-size: 10px;
}
.pi-preview .pi-contact strong {
    display: block; font-size: 10px; text-transform: uppercase; margin-bottom: 2px;
}
`;

const ProformaInvoicePreview = ({
    customer = {},
    rows = [],
    discountAmount = 0,
    deliveryCharge = 0,
    subTotal = 0,
    grandTotal = 0,
    totalQty = 0,
    totalGstAmount = 0,
    proformaNumber,
    proformaDate,
}) => {
    const emptyRows = Math.max(0, MIN_ITEM_ROWS - rows.length);
    const heading = customer.business_name?.trim() || customer.customer_name?.trim() || 'Customer Name';
    const phones = [customer.customer_phone, customer.customer_alternate_phone]
        .map((p) => String(p || '').trim())
        .filter(Boolean);

    return (
        <div className="pi-preview">
            <style>{CSS}</style>

            {/* Header */}
            <div className="pi-header">
                <div className="pi-company">
                    <h2>{COMPANY.name}</h2>
                    {COMPANY.addressLines.map((line) => <p key={line}>{line}</p>)}
                    <p>GSTIN: {COMPANY.gstin}</p>
                    <p>PAN: {COMPANY.pan}</p>
                </div>
                <div className="pi-title">
                    <h1><em>PROFORMA INVOICE</em></h1>
                </div>
            </div>
            <div className="pi-green-bar" />

            {/* Customer & meta */}
            <div className="pi-customer-section">
                <div className="pi-customer">
                    <h3>{heading}</h3>
                    <p>Contact No: {phones.length ? phones.join(' | ') : '—'}</p>
                    {customer.customer_address?.trim() && <p>{customer.customer_address}</p>}
                    {customer.customer_gst?.trim() && <p className="pi-gst">GST No. {customer.customer_gst}</p>}
                </div>
                <div className="pi-meta">
                    <p className="pi-meta-label">Proforma No</p>
                    <p className="pi-meta-value">{proformaNumber || 'Auto-generated'}</p>
                    <p className="pi-meta-label">Date</p>
                    <p className="pi-meta-value">{formatDate(proformaDate)}</p>
                </div>
            </div>

            {/* Items */}
            <table className="pi-items">
                <thead>
                    <tr>
                        <th>S No</th>
                        <th>Product</th>
                        <th>HSN</th>
                        <th>Qty</th>
                        <th>Rate</th>
                        <th>GST</th>
                        <th>GST Amt</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((item, index) => (
                        <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{item.display_name || <span style={{ color: '#bbb' }}>Product name</span>}</td>
                            <td>{item.hsn_code || ''}</td>
                            <td>{item.quantity}</td>
                            <td className="pi-nowrap">₹{money(item.rate)}</td>
                            <td>{Number(item.gst_percent) || 0}%</td>
                            <td className="pi-nowrap">₹{money(item.gst_amount)}</td>
                            <td className="pi-nowrap">₹{money(item.amount)}</td>
                        </tr>
                    ))}
                    {Array.from({ length: emptyRows }).map((_, i) => (
                        <tr className="pi-empty-row" key={`empty-${i}`}>
                            <td>&nbsp;</td><td /><td /><td /><td /><td /><td /><td />
                        </tr>
                    ))}
                    <tr className="pi-totals-row">
                        <td /><td /><td />
                        <td>{totalQty}</td>
                        <td /><td />
                        <td className="pi-nowrap">₹{money(totalGstAmount)}</td>
                        <td className="pi-nowrap">₹{money(subTotal)}</td>
                    </tr>
                </tbody>
            </table>

            {/* Summary */}
            <div className="pi-summary">
                <table>
                    <tbody>
                        <tr>
                            <td style={{ width: '60%' }} />
                            <td className="pi-summary-label">SUB TOTAL</td>
                            <td className="pi-summary-value">₹{money(subTotal)}</td>
                        </tr>
                        {Number(discountAmount) > 0 && (
                            <tr>
                                <td />
                                <td className="pi-summary-label">DISCOUNT</td>
                                <td className="pi-summary-value">₹{money(discountAmount)}</td>
                            </tr>
                        )}
                        {Number(deliveryCharge) > 0 && (
                            <tr>
                                <td />
                                <td className="pi-summary-label">COD CHARGES</td>
                                <td className="pi-summary-value">₹{money(deliveryCharge)}</td>
                            </tr>
                        )}
                        <tr className="pi-grand-total">
                            <td />
                            <td className="pi-summary-label">GRAND TOTAL</td>
                            <td className="pi-summary-value">₹{money(grandTotal)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pi-notice">
                This is a Proforma Invoice and is not a demand for payment. Prices and availability are subject to change.
            </div>

            {/* Footer */}
            <div className="pi-footer">
                <div className="pi-terms">
                    <h4>Terms and Conditions</h4>
                    <ul>
                        <li>This proforma invoice is valid for 15 days from the date of issue.</li>
                        <li>Prices are subject to change without prior notice.</li>
                        <li>*Subject to 'Indore' Jurisdiction Only. E.&amp;O.E*</li>
                    </ul>
                </div>
                <div className="pi-branding">
                    <img src={COMPANY.logo} alt="Rashail Agro" />
                    <p className="pi-brand-tagline">Empowering Farming with Technology</p>
                </div>
            </div>

            <div className="pi-contact">
                <div><strong>WhatsApp</strong>{COMPANY.whatsapp}</div>
                <div><strong>Email</strong>{COMPANY.email}</div>
                <div><strong>Website</strong>{COMPANY.website}</div>
            </div>
        </div>
    );
};

export default ProformaInvoicePreview;
